import { API_BASE_URL } from '../config/api';

const ACCESS_TOKEN_KEY = 'admin_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';
const AUTH_EXPIRED_CODES = new Set([1413, 1414]);
const NETWORK_ERROR_MESSAGE = '网络连接失败，请检查网络后重试';
const NETWORK_ERROR_PATTERNS = ['Failed to fetch', 'NetworkError', 'Load failed'];

interface ApiBody<T = unknown> {
  code?: number;
  message?: string;
  data?: T;
  error?: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: number,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiError) {
    if (error.status === 401) return '登录状态已失效，请重新登录';
    if (error.status === 403 && !error.message.trim()) return '当前账号没有执行此操作的权限';
    if (error.status === 429 && !error.message.trim()) return '操作过于频繁，请稍后重试';
    return error.message.trim() || fallback;
  }
  if (
    error instanceof TypeError &&
    NETWORK_ERROR_PATTERNS.some(pattern => error.message.includes(pattern))
  ) {
    return NETWORK_ERROR_MESSAGE;
  }
  return error instanceof Error && error.message.trim() ? error.message : fallback;
};

export const getStoredAuthTokens = () => ({
  accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
  refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
});

export const setStoredAuthTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearStoredAuthTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

const readJson = async <T>(response: Response): Promise<ApiBody<T>> => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text) as ApiBody<T>;
  } catch {
    return { message: text };
  }
};

const buildHeaders = (headers?: HeadersInit) => {
  const nextHeaders = new Headers(headers);
  const { accessToken } = getStoredAuthTokens();

  if (!nextHeaders.has('Content-Type')) nextHeaders.set('Content-Type', 'application/json');
  if (!nextHeaders.has('Accept-Language')) nextHeaders.set('Accept-Language', 'zh-CN');
  if (accessToken) nextHeaders.set('Authorization', `Bearer ${accessToken}`);

  return nextHeaders;
};

let refreshPromise: Promise<string> | null = null;

export const refreshAdminSession = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refreshToken } = getStoredAuthTokens();
    if (!refreshToken) {
      clearStoredAuthTokens();
      throw new ApiError(401, 'No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'zh-CN',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const body = await readJson<{ session?: { access_token?: string; refresh_token?: string } }>(
      response
    );

    if (!response.ok) {
      if (response.status === 401) clearStoredAuthTokens();
      throw new ApiError(response.status, body.message || 'Session expired', body.code, body.error);
    }

    const session = body.data?.session;
    if (!session?.access_token || !session?.refresh_token) {
      clearStoredAuthTokens();
      throw new ApiError(401, 'Invalid refresh response');
    }

    setStoredAuthTokens(session.access_token, session.refresh_token);
    return session.access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const createApiError = async (response: Response) => {
  const body = await readJson(response);
  return new ApiError(
    response.status,
    body.message || `HTTP error! status: ${response.status}`,
    body.code,
    body.error
  );
};

export const isAuthExpiredError = (error: unknown) =>
  error instanceof ApiError &&
  (error.status === 401 || (typeof error.code === 'number' && AUTH_EXPIRED_CODES.has(error.code)));

export const adminRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  config: { retryOnAuthError?: boolean } = {}
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  const retryOnAuthError = config.retryOnAuthError !== false;
  const response = await fetchWithAdminAuth(url, options, retryOnAuthError);

  if (!response.ok) throw await createApiError(response);

  const body = await readJson<T>(response);
  return (body.data ?? body) as T;
};

const fetchWithAdminAuth = async (url: string, options: RequestInit, retryOnAuthError: boolean) => {
  const requestInit = () => ({
    ...options,
    headers: buildHeaders(options.headers),
  });
  let response = await fetch(url, requestInit());
  if (response.status === 401 && retryOnAuthError) {
    await refreshAdminSession();
    response = await fetch(url, requestInit());
  }
  return response;
};
